import { useQuery } from "react-query";
import { useState, useEffect, useRef, useCallback } from "react";
import { NormalizeTheses } from "utils/normalizer";
import { payamLibService } from "services/client";
import { THESIS_ITEM_TYPE_ID } from "utils";

const PAGE_SIZE = 100;
const DELAY_BETWEEN_PAGES = 100; // تأخیر بین بارگذاری صفحات

export const useThesis = (branches, itemTypeId) => {
  const [allData, setAllData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);

  // برای track کردن mount/unmount component
  const isMountedRef = useRef(true);

  // مرتب‌سازی بر اساس تاریخ دفاع
  const sortThesesByDate = useCallback((theses) => {
    if (!theses || theses.length === 0) return theses;

    return [...theses].sort((a, b) => {
      // بررسی وجود تاریخ معتبر برای هر دو آیتم
      const dateA = a?.defencDate ? new Date(a.defencDate) : null;
      const dateB = b?.defencDate ? new Date(b.defencDate) : null;

      const isValidDateA = dateA && !isNaN(dateA.getTime());
      const isValidDateB = dateB && !isNaN(dateB.getTime());

      // اگر A تاریخ معتبر دارد و B ندارد، A اول بیاید
      if (isValidDateA && !isValidDateB) return -1;

      // اگر B تاریخ معتبر دارد و A ندارد، B اول بیاید
      if (!isValidDateA && isValidDateB) return 1;

      // اگر هر دو تاریخ معتبر دارند، جدیدترین اول
      if (isValidDateA && isValidDateB) {
        return dateB.getTime() - dateA.getTime();
      }

      // اگر هیچکدام تاریخ معتبر ندارند، ترتیب اصلی حفظ شود
      return 0;
    });
  }, []);

  // بارگذاری صفحه اول با React Query
  const {
    data: firstPage,
    isLoading: isFirstLoading,
    error: firstPageError,
  } = useQuery({
    queryKey: ["thesis", branches],
    queryFn: () =>
      payamLibService.getTheses({
        ItemTypeId: itemTypeId || THESIS_ITEM_TYPE_ID?.FA,
        Branches: branches,
        PageNumber: 1,
        PageSize: PAGE_SIZE,
      }),
    select: (data) => {
      const normalized = NormalizeTheses(data);
      // مرتب‌سازی داده‌های صفحه اول
      if (normalized?.Items) {
        normalized.Items = sortThesesByDate(normalized.Items);
      }
      return normalized;
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    enabled: !!branches && branches.length > 0,
    onError: (error) => {
      if (isMountedRef.current) {
        setHasError(true);
        console.error("خطا در بارگذاری صفحه اول:", error);
      }
    },
  });

  // بارگذاری صفحات بعدی
  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;

    if (!firstPage?.Items || hasError) return;

    // فقط اگر component هنوز mount شده، state رو آپدیت کن
    if (isMountedRef.current) {
      setAllData(firstPage.Items);
    }

    const totalCount = firstPage.TotalCount || 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    // اگر فقط یک صفحه داریم
    if (totalPages <= 1) {
      return;
    }

    const loadRemainingPages = async () => {
      // چک کردن mount بودن قبل از شروع
      if (!isMountedRef.current) return;

      setIsLoadingMore(true);
      setHasError(false);

      try {
        for (let page = 2; page <= totalPages; page++) {
          // چک کردن mount بودن در هر iteration
          if (!isMountedRef.current) break;

          try {
            const nextPage = await payamLibService.getTheses({
              ItemTypeId: itemTypeId || THESIS_ITEM_TYPE_ID?.FA,
              Branches: branches,
              PageNumber: page,
              PageSize: PAGE_SIZE,
            });

            // چک کردن مجدد قبل از پردازش
            if (!isMountedRef.current) break;

            const normalizedNextPage = NormalizeTheses(nextPage);
            const nextPageItems = normalizedNextPage?.Items || [];

            if (nextPageItems.length > 0 && isMountedRef.current) {
              // مرتب‌سازی صفحه جدید
              const sortedNextPageItems = sortThesesByDate(nextPageItems);

              // آپدیت state با داده‌های مرتب‌شده
              setAllData((prev) => {
                // ترکیب داده‌های قبلی و جدید
                const combined = [...prev, ...sortedNextPageItems];
                // مرتب‌سازی کل داده‌ها
                return sortThesesByDate(combined);
              });

              setCurrentPage(page);
            }

            // تأخیر برای جلوگیری از فشار به سرور
            await new Promise((resolve) => {
              if (isMountedRef.current) {
                setTimeout(resolve, DELAY_BETWEEN_PAGES);
              } else {
                resolve();
              }
            });
          } catch (pageError) {
            console.error(`خطا در بارگذاری صفحه ${page}:`, pageError);
            if (isMountedRef.current) {
              setHasError(true);
            }
            break;
          }
        }
      } finally {
        // فقط اگر component هنوز mount شده، loading رو false کن
        if (isMountedRef.current) {
          setIsLoadingMore(false);
        }
      }
    };

    // شروع بارگذاری صفحات بعدی
    loadRemainingPages();

    // Cleanup function برای وقتی component unmount می‌شود
    return () => {
      isMountedRef.current = false;
    };
  }, [firstPage, branches, hasError, sortThesesByDate]);

  // Cleanup effect اضافی برای اطمینان
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    data: allData,
    isLoading: isFirstLoading,
    isLoadingMore,
    currentPage,
    totalCount: firstPage?.TotalCount || 0,
    hasError: hasError || !!firstPageError,
    error: firstPageError,
  };
};

export default useThesis;
